import React from 'react'
import { Button } from '../../components/simple/button'
import { FloatingLoader } from '../../components/simple/loader'
import { useRequest } from '../../request'
import { Tab, Tabs } from '../../layouts/components/tabs'
import { UploadTab } from './tabs/upload'
import { Teacher } from '../../providers/context'
import { SimpleLayout } from '../../layouts/simple'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { AdditionalDataTab } from './tabs/additionalData'
import { SendMessageTab } from './tabs/sendMessage'
import { ProcessTab } from './tabs/processTab'
import { StatsTab } from './tabs/stats'

export type Assessment = {
  id: number
  name: string
  files: {
    name: string
    size: number
  }[]
  identifier?: string
  processing: boolean
  published: boolean
  processed: {
    name: string
    syncID: string
    previewLink: string
  }[]
  processingNotes?: string
  needsAdditionalData?: string[]
  school?: string
  hasBeenSent: boolean
}

export const AssessmentDashboard: React.FC<{ id: number; user: Teacher }> = ({ id, user }) => {
  const {
    data: assessment,
    loading,
    reload,
  } = useRequest<Assessment>(
    '/frontend_api/getAssessment',
    React.useMemo(
      () => ({
        id,
      }),
      [id]
    )
  )

  return (
    <SimpleLayout verticalScroll>
      <div className={`m-4 flex flex-col gap-2 ${assessment && loading ? 'pointer-events-none opacity-50' : ''}`}>
        {!assessment ? (
          <FloatingLoader />
        ) : (
          <>
            <div className="mx-4 flex justify-between self-stretch">
              <div className="flex items-end gap-4">
                <div className="text-2xl font-semibold">{assessment.name}</div>
                {assessment.school && <div className="text-lg font-semibold opacity-50">{assessment.school}</div>}
              </div>
              <div className="flex gap-2">
                <Button link={`/assessment/edit/${id}`} disabled={assessment.processing}>
                  <Badge className="mr-2 self-center" badgeStyle={BadgeStyle.warning}>
                    {assessment.published ? 'Published' : 'Not Published'}
                  </Badge>
                  Edit Assessment
                </Button>
              </div>
            </div>

            {assessment.processing ? (
              <div className="m-2 flex flex-col items-center gap-4 self-stretch rounded bg-warning p-4">
                This assessment is currently processing uploaded data. Please check back soon.
                <Button className="mt-2 opacity-70" onClick={reload}>
                  Refresh
                </Button>
              </div>
            ) : (
              <Tabs className="mt-2">
                <Tab tab="upload" label="Upload Documents">
                  <UploadTab assessment={assessment} reloadAssessment={reload} />
                </Tab>
                {assessment.needsAdditionalData && (
                  <Tab tab="additionalData" label="Additional Student Data">
                    <AdditionalDataTab assessment={assessment} user={user} />
                  </Tab>
                )}
                <Tab
                  tab="process"
                  label="Process and Preview"
                  disabled={!assessment.identifier || !assessment.files.length}
                  tooltip={
                    assessment.identifier && assessment.files.length
                      ? undefined
                      : 'You need to upload files and pick an identifier first'
                  }
                >
                  <ProcessTab assessment={assessment} reloadAssessment={reload} />
                </Tab>

                <Tab tab="sendMessage" label="Send Message">
                  <SendMessageTab assessment={assessment} />
                </Tab>
                {assessment.hasBeenSent && (
                  <Tab tab="stats" label="Stats">
                    <StatsTab assessment={assessment} />
                  </Tab>
                )}
              </Tabs>
            )}
          </>
        )}
      </div>
    </SimpleLayout>
  )
}
